import * as React from 'react';
import { Translate } from 'react-localize-redux';
import PageLoader from '../../../components_v2/pageLoader/PageLoader';
import Popup from '../../../components_v2/popup/Popup';
import { PopupMode } from '../../../components_v2/popup/model/Model';
import { PopupHeader } from '../../../components_v2/popup/PopupCreation';
import PopupDeletion from '../../../components_v2/popup/PopupDeletion';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { translateToString } from '../../../styles/global/translate';
import { launchToastError } from '../../forms/visual-form-editor/visualFormEditor';
import { deleteVisionImage, getVisionImages, uploadPackshot, VisionImage } from './actions';
import { Body, Empty, Grid, Hint, SectionTitle, Thumb, ThumbDelete } from './style';
import { Trash } from 'lucide-react';

/** The product's shelf-vision images: packshots (addable here) and field crops (from the app). */
export default function VisionPopup(props: {
	isOpen: boolean
	onClose: () => void
	product?: { uuid: string, name: string }
}): JSX.Element {
	const { isOpen, onClose, product } = props;
	const [images, setImages] = React.useState<VisionImage[]>([]);
	const [loading, setLoading] = React.useState(false);
	const [uploading, setUploading] = React.useState(0);
	const [deleting, setDeleting] = React.useState<VisionImage>();
	const fileInput = React.useRef<HTMLInputElement>(null);

	const refresh = React.useCallback(() => {
		if (!product) return;
		setLoading(true);
		getVisionImages(product.uuid)
			// the server filters by product; keep only this product's rows whatever it answers
			.then(list => setImages(list.filter(i => i.sku_id === product.uuid)))
			.catch(() => launchToastError(translateToString('products.vision_load_failed')))
			.finally(() => setLoading(false));
	}, [product?.uuid]);

	React.useEffect(() => {
		if (isOpen) refresh();
		else setImages([]);
	}, [isOpen, refresh]);

	const onFiles = async(files: FileList | null) => {
		if (!product || !files?.length) return;
		setUploading(files.length);
		let failed = 0;
		for (const file of Array.from(files)) {
			try {
				await uploadPackshot(product.uuid, file);
			} catch (e) {
				console.log(e);
				failed++;
			}
			setUploading(n => n - 1);
		}
		if (failed) launchToastError(translateToString('products.vision_upload_failed'));
		refresh();
	};

	const remove = (image: VisionImage) => {
		deleteVisionImage(image.id)
			.then(() => setImages(list => list.filter(i => i.id !== image.id)))
			.catch(() => launchToastError(translateToString('products.vision_delete_failed')));
	};

	const packshots = images.filter(i => i.kind === 'packshot');
	const field = images.filter(i => i.kind === 'field');

	const grid = (list: VisionImage[], empty: string) => list.length
		? <Grid>
			{list.map(image => <Thumb key={image.id} background={image.url ?? ''} onClick={() => image.url && window.open(image.url, '_blank')} title={image.id}>
				<ThumbDelete onClick={e => { e.stopPropagation(); setDeleting(image); }} title={translateToString('global.delete')}>
					<Trash size={14} />
				</ThumbDelete>
			</Thumb>)}
		</Grid>
		: <Empty><Translate id={empty} /></Empty>;

	return <Popup
		isOpen={isOpen}
		onClickOut={onClose}
		popupStyle={{ animate: true, height: '100vh', top: '0' }}
		popupMode={PopupMode.Details}
		disableOutClick={deleting !== undefined}
		disableEsc={deleting !== undefined}
	>
		<PopupHeader title={translateToString('products.vision_title', [['NAME', product?.name ?? '']])} onClose={onClose} />
		<Body>
			<Hint><Translate id='products.vision_hint' /></Hint>
			<div>
				<SectionTitle>
					<span><Translate id='products.vision_packshots' /> ({packshots.length})</span>
					<DefaultButton height='28px' padding='0 12px' disabled={uploading > 0 || !product} onClick={() => fileInput.current?.click()}>
						{uploading > 0 ? <PageLoader size={14} /> : <Translate id='products.vision_add_packshot' />}
					</DefaultButton>
					<input ref={fileInput} type='file' accept='image/*' multiple hidden onChange={e => { onFiles(e.target.files); e.target.value = ''; }} />
				</SectionTitle>
				{loading && !images.length ? <PageLoader size={20} /> : grid(packshots, 'products.vision_no_packshots')}
			</div>
			<div>
				<SectionTitle><span><Translate id='products.vision_field_crops' /> ({field.length})</span></SectionTitle>
				{loading && !images.length ? null : grid(field, 'products.vision_no_field')}
			</div>
		</Body>
		<PopupDeletion
			isOpen={deleting !== undefined}
			records={1}
			translationKey='products.vision_image'
			noVerification
			onClickOut={() => setDeleting(undefined)}
			onValidation={() => {
				if (deleting) remove(deleting);
				setDeleting(undefined);
			}}
		/>
	</Popup>;
}
